import ReservationSidebar from '../Commun/ReservationSidebar';
import ReservationHeader from '../Commun/ReservationHeader';
import { useEffect, useState } from 'react';
import { Property, ReserBusinessSource } from '../../Services/Service';
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { fetchBusinessMonthlyReport, fetchBusinessPropertyMonthlyReport } from '../../Redux/Slices/BusinessSourceReportSlice';

export const BusinessMonthlyReport = () => {
    const dispatch = useDispatch();

    const [propertyList, setPropertyList] = useState([]);
    const [selectedProperty, setSelectedProperty] = useState([]);
    const [selectedYear, setSelectedYear] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState([]);
    const [sources, setSources] = useState([]);
    const [selectedSource, setSelectedSource] = useState([]);
    const [reportType, setReportType] = useState("source"); // default

    const loading = useSelector(state => state.businessSourceReport.loading);

    // 🔹 Fetch Sources
    useEffect(() => {
        const fetchBusinessSource = async () => {
            try {
                const data = await ReserBusinessSource(333333);
                if (Array.isArray(data)) setSources(data);
            } catch (error) {
                console.log(error);
            }
        };
        fetchBusinessSource();
    }, []);

    // 🔹 Default values
    useEffect(() => {
        fetchProperty();

        const now = new Date();

        setSelectedYear([{
            value: now.getFullYear(),
            label: now.getFullYear().toString()
        }]);

        setSelectedMonth([{
            value: now.getMonth() + 1,
            label: now.toLocaleString('default', { month: 'long' })
        }]);
    }, []);

    const fetchProperty = async () => {
        const data = await Property();
        setPropertyList(data);

        setSelectedProperty(
            data.map(p => ({
                value: p.PropertyID,
                label: p.PropertyName
            }))
        );
    };

    // 🔹 Options
    const propertyOptions = propertyList.map(p => ({
        value: p.PropertyID,
        label: p.PropertyName
    }));

    const yearOptions = Array.from({ length: 10 }, (_, i) => {
        const y = new Date().getFullYear() - i;
        return { value: y, label: y.toString() };
    });

    const monthOptions = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ].map((m, i) => ({ value: i + 1, label: m }));

    const sourceOptions = sources
        ?.filter(s => s.BONGroupCode && s.BONBusinessSource)
        .map(s => ({
            value: s.BONGroupCode,
            label: s.BONBusinessSource
        }));

    // 🔹 Handlers
    const handlePropertyChange = v => setSelectedProperty(v || []);
    const handleYearChange = v => setSelectedYear(v || []);
    const handleMonthChange = v => setSelectedMonth(v || []);
    const handleSourceChange = v => setSelectedSource(v || []);

    // 🔹 Generate PDF page
    const generateMultiMonthPage = (doc, data, selectedMonths, yearObj, today, isFirstPage, selectedProperty) => {

        const pageWidth = doc.internal.pageSize.getWidth();

        if (!isFirstPage) doc.addPage();

        // 🔹 Title
        doc.setFontSize(16);
        doc.text("Source Monthly Report", pageWidth / 2, 10, { align: "center" });

        doc.setFontSize(10);
        doc.text(`Generated: ${today}`, pageWidth - 10, 10, { align: "right" });

        // =========================
        // 🔹 PROPERTY BOX (FINAL FIXED)
        // =========================

        let propertyList = [];

        if (selectedProperty.length === propertyOptions.length) {
            propertyList = ["All Property"];
        } else {
            propertyList = selectedProperty.map(p => p.label);
        }

        const startX = 12;
        const boxY = 14;

        const label = "Property:";
        const labelWidth = doc.getTextWidth(label);

        const textStartX = startX + labelWidth + 6;

        const lineHeight = 5;

        // 🔥 SMART AUTO WIDTH WRAP (FINAL FIX)
        const rowsProp = [];
        let currentLine = "";
        let currentWidth = 0;

        const maxWidth = pageWidth - textStartX - 12; // safe margin

        propertyList.forEach((name, index) => {

            const isLast = index === propertyList.length - 1;

            // ✅ Equal spacing both sides
            const separator = isLast ? "" : "  |  ";
            const text = name + separator;

            const textWidth = doc.getTextWidth(text);

            if (currentWidth + textWidth <= maxWidth) {
                currentLine += text;
                currentWidth += textWidth;
            } else {
                rowsProp.push(currentLine.trim());
                currentLine = text;
                currentWidth = textWidth;
            }
        });

        if (currentLine) {
            rowsProp.push(currentLine.trim());
        }

        // ✅ Tight height (no gap)
        const boxHeight = (rowsProp.length * lineHeight) + 1.5;

        // 🔘 Border
        doc.setDrawColor(180, 180, 180);
        doc.setLineWidth(0.5);
        doc.rect(10, boxY, pageWidth - 20, boxHeight);

        // 🔹 Label
        doc.setFont(undefined, "bold");
        doc.setFontSize(10);
        doc.text("Property:", startX, boxY + 4);

        // 🔹 Values
        doc.setFont(undefined, "normal");

        let y = boxY + 4;

        rowsProp.forEach(line => {
            doc.text(line, textStartX, y);
            y += lineHeight;
        });

        const startY = y + 3;

        // =========================
        // 🔢 NUMBER FORMAT
        // =========================
        const formatNumber = (num) => {
            if (!num) return "0";
            return Number(num).toLocaleString("en-IN");
        };

        const months = selectedMonths.map(m => m.value).sort((a, b) => a - b);
        const isMoreThan6 = months.length > 6;

        // =========================
        // ✅ CASE 1: ≤ 6 MONTHS
        // =========================
        if (!isMoreThan6) {

            const monthLabels = months.map(m => {
                const d = new Date(yearObj.value, m - 1);
                return d.toLocaleString('default', { month: 'short', year: 'numeric' });
            });

            const headerRow1 = [
                { content: "Business Source", rowSpan: 2, styles: { halign: 'center', valign: 'middle' } },
                ...monthLabels.map(label => ({
                    content: label,
                    colSpan: 2,
                    styles: { halign: 'center' }
                }))
            ];

            const headerRow2 = months.flatMap(() => ([
                { content: "RM NTS", halign: 'center' },
                { content: "REV.", halign: 'center' }
            ]));

            const rows = data.map(item => [
                item.BusinessSource || "",
                ...months.flatMap(m => ([
                    formatNumber(item[`OCC_${m}`] || 0),
                    formatNumber(item[`REV_${m}`] || 0)
                ]))
            ]);

            const totalCols = 1 + (months.length * 2);
            const totalWidth = pageWidth - 20;

            const firstColWidth = 30;
            const otherColWidth = (totalWidth - firstColWidth) / (totalCols - 1);

            const columnStyles = {
                0: { cellWidth: firstColWidth, halign: 'left', valign: 'middle' }
            };

            for (let i = 1; i < totalCols; i++) {
                columnStyles[i] = { cellWidth: otherColWidth, halign: 'right' };
            }

            autoTable(doc, {
                startY,
                head: [headerRow1, headerRow2],
                body: rows,
                theme: 'grid',

                styles: {
                    fontSize: 7,
                    cellPadding: 1.5,
                    lineWidth: 0.2
                },

                headStyles: {
                    fillColor: [41, 128, 185],
                    textColor: 255
                },

                columnStyles,
                margin: { left: 10, right: 10 }
            });
        }

        // =========================
        // 🔥 CASE 2: > 6 MONTHS
        // =========================
        else {

            const monthLabels = months.map(m => {
                const d = new Date(yearObj.value, m - 1);
                return d.toLocaleString('default', { month: 'short', year: 'numeric' });
            });

            const headerRow = [
                "Business Source",
                "Type",
                ...monthLabels
            ];

            const rows = [];

            data.forEach(item => {

                rows.push([
                    {
                        content: item.BusinessSource || "",
                        rowSpan: 2,
                        styles: { valign: 'middle', halign: 'left' }
                    },
                    "RM NTS",
                    ...months.map(m => formatNumber(item[`OCC_${m}`] || 0))
                ]);

                rows.push([
                    "REV.",
                    ...months.map(m => formatNumber(item[`REV_${m}`] || 0))
                ]);
            });

            const totalCols = 2 + months.length;
            const totalWidth = pageWidth - 20;

            const firstColWidth = 30;
            const secondColWidth = 15;
            const otherColWidth = (totalWidth - firstColWidth - secondColWidth) / months.length;

            const columnStyles = {
                0: { cellWidth: firstColWidth, halign: 'left', valign: 'middle' },
                1: { cellWidth: secondColWidth, halign: 'center' }
            };

            for (let i = 2; i < totalCols; i++) {
                columnStyles[i] = { cellWidth: otherColWidth, halign: 'right' };
            }

            autoTable(doc, {
                startY,
                head: [headerRow],
                body: rows,
                theme: 'grid',

                styles: {
                    fontSize: 7,
                    cellPadding: 1.5,
                    lineWidth: 0.2,
                    valign: 'middle'
                },

                headStyles: {
                    fillColor: [41, 128, 185],
                    textColor: 255
                },

                columnStyles,
                margin: { left: 10, right: 10 }
            });
        }
    };

    // 🔹 Generate Property PDF page
    const generateMultiMonthPageProperty = (
        doc,
        data,
        selectedMonths,
        yearObj,
        today,
        isFirstPage,
        SourceNames
    ) => {

        const pageWidth = doc.internal.pageSize.getWidth();

        if (!isFirstPage) {
            doc.addPage();
        }

        // 🔹 Title
        doc.setFontSize(16);
        doc.text("Property Source Monthly Report", pageWidth / 2, 10, { align: "center" });

        doc.setFontSize(10);
        doc.text(`Generated: ${today}`, pageWidth - 10, 10, { align: "right" });

        // ✅ Source Name
        if (SourceNames && SourceNames.length > 0) {
            doc.text(`Source: ${SourceNames.join(", ")}`, 10, 16);
        }

        const startY = SourceNames?.length ? 22 : 20;

        // 🔹 FORMAT NUMBER
        const formatNumber = (num) => {
            if (!num) return "0";
            return Number(num).toLocaleString("en-IN");
        };

        const months = selectedMonths.map(m => m.value).sort((a, b) => a - b);

        const isMoreThan6 = months.length > 6;

        // =========================
        // ✅ CASE 1: ≤ 6 MONTHS
        // =========================
        if (!isMoreThan6) {

            const monthLabels = months.map(m => {
                const d = new Date(yearObj.value, m - 1);
                return d.toLocaleString('default', { month: 'short', year: 'numeric' });
            });

            const headerRow1 = [
                { content: "Property Name", rowSpan: 2, styles: { halign: 'center', valign: 'middle' } },
                ...monthLabels.map(label => ({
                    content: label,
                    colSpan: 2,
                    styles: { halign: 'center' }
                }))
            ];

            const headerRow2 = months.flatMap(() => ([
                { content: "RM NTS", styles: { halign: 'center', fillColor: [41, 128, 185] } },
                { content: "REV.", styles: { halign: 'center', fillColor: [41, 128, 185] } }
            ]));

            const rows = data.map(item => [
                item.PropertyName || "",
                ...months.flatMap(m => ([
                    formatNumber(item[`OCC_${m}`] || 0),
                    formatNumber(item[`REV_${m}`] || 0)
                ]))
            ]);

            // 🔹 WIDTH FIX
            const totalCols = 1 + (months.length * 2);
            const totalWidth = pageWidth - 20;

            const firstColWidth = 40;
            const otherColWidth = (totalWidth - firstColWidth) / (totalCols - 1);

            const columnStyles = {
                0: { cellWidth: firstColWidth, halign: 'left', valign: 'middle' }
            };

            for (let i = 1; i < totalCols; i++) {
                columnStyles[i] = { cellWidth: otherColWidth, halign: 'right' };
            }

            autoTable(doc, {
                startY,
                head: [headerRow1, headerRow2],
                body: rows,
                theme: 'grid',
                styles: {
                    fontSize: 7,
                    cellPadding: 1.5,
                    lineWidth: 0.2
                },
                headStyles: {
                    fillColor: [41, 128, 185],
                    textColor: 255
                },
                columnStyles,
                margin: { left: 10, right: 10 }
            });
        }

        // =========================
        // 🔥 CASE 2: > 6 MONTHS
        // =========================
        else {

            const monthLabels = months.map(m => {
                const d = new Date(yearObj.value, m - 1);
                return d.toLocaleString('default', { month: 'short', year: 'numeric' });
            });

            const headerRow = [
                "Property Name",
                "Type",
                ...monthLabels
            ];

            const rows = [];

            data.forEach(item => {

                // 🔹 RM NTS
                rows.push([
                    {
                        content: item.PropertyName || "",
                        rowSpan: 2,
                        styles: { valign: 'middle', halign: 'left' }
                    },
                    { content: "RM NTS" },
                    ...months.map(m => formatNumber(item[`OCC_${m}`] || 0))
                ]);

                // 🔹 REV
                rows.push([
                    { content: "REV." },
                    ...months.map(m => formatNumber(item[`REV_${m}`] || 0))
                ]);
            });

            // 🔹 WIDTH FIX
            const totalCols = 2 + months.length;
            const totalWidth = pageWidth - 20;

            const firstColWidth = 40;
            const secondColWidth = 20;
            const otherColWidth = (totalWidth - firstColWidth - secondColWidth) / months.length;

            const columnStyles = {
                0: { cellWidth: firstColWidth, halign: 'left', valign: 'middle' },
                1: { cellWidth: secondColWidth, halign: 'left' }
            };

            for (let i = 2; i < totalCols; i++) {
                columnStyles[i] = { cellWidth: otherColWidth, halign: 'right' };
            }

            autoTable(doc, {
                startY,
                head: [headerRow],
                body: rows,
                theme: 'grid',
                styles: {
                    fontSize: 7,
                    cellPadding: 1.5,
                    lineWidth: 0.2,
                    valign: 'middle'
                },
                headStyles: {
                    fillColor: [41, 128, 185],
                    textColor: 255
                },
                columnStyles,
                margin: { left: 10, right: 10 }
            });
        }
    };

    // const handleReport = async (e) => {
    //     e.preventDefault();

    //     if (!selectedProperty.length || !selectedYear.length || !selectedMonth.length) {
    //         alert("Please select all fields");
    //         return;
    //     }

    //     // 🔥 SOURCE REPORT
    //     if (reportType === "source") {

    //         const PID = selectedProperty.map(p => p.value);
    //         const Codes = selectedSource.map(s => s.value);

    //         const doc = new jsPDF("landscape");
    //         const today = new Date().toLocaleDateString("en-GB", {
    //             day: "2-digit",
    //             month: "long",
    //             year: "numeric"
    //         });

    //         let isFirstPage = true;

    //         for (const yearObj of selectedYear) {

    //             const monthsArray = selectedMonth.map(m => m.value);

    //             let finalData = [];

    //             const sourceLoop = Codes.length ? Codes : [""];

    //             for (const sourceCode of sourceLoop) {

    //                 const payload = {
    //                     PID: `[${PID}]`,
    //                     Year: yearObj.value,
    //                     Month: `[${monthsArray}]`,
    //                     Code: sourceCode
    //                 };

    //                 const result = await dispatch(fetchBusinessMonthlyReport(payload));
    //                 const data = result.payload || [];

    //                 finalData = [...finalData, ...data];
    //             }

    //             const uniqueData = Object.values(
    //                 finalData.reduce((acc, item) => {
    //                     acc[item.BusinessSource] = item;
    //                     return acc;
    //                 }, {})
    //             );

    //             generateMultiMonthPage(
    //                 doc,
    //                 uniqueData,
    //                 selectedMonth,
    //                 yearObj,
    //                 today,
    //                 isFirstPage,
    //                 selectedProperty
    //             );

    //             isFirstPage = false;
    //         }

    //         doc.save("Source_Monthly_Report.pdf");
    //     }

    //     // 🔥 PROPERTY REPORT
    //     else {

    //         const PID = selectedProperty.map(p => p.value);
    //         const Codes = selectedSource.map(s => s.value);
    //         const SourceNames = selectedSource.map(s => s.label);

    //         const doc = new jsPDF("landscape");
    //         const today = new Date().toLocaleDateString("en-GB", {
    //             day: "2-digit",
    //             month: "long",
    //             year: "numeric"
    //         });

    //         let isFirstPage = true;

    //         for (const yearObj of selectedYear) {

    //             const monthsArray = selectedMonth.map(m => m.value);

    //             const payload = {
    //                 PID: `[${PID}]`,
    //                 Year: yearObj.value,
    //                 Month: `[${monthsArray}]`,
    //                 Code: Codes.length ? Codes[0] : ""
    //             };

    //             const result = await dispatch(fetchBusinessPropertyMonthlyReport(payload));
    //             const data = result.payload || [];

    //             generateMultiMonthPageProperty(   // 👈 naam change karo
    //                 doc,
    //                 data,
    //                 selectedMonth,
    //                 yearObj,
    //                 today,
    //                 isFirstPage,
    //                 SourceNames
    //             );

    //             isFirstPage = false;
    //         }

    //         doc.save("Property_Source_Monthly_Report.pdf");
    //     }
    // };

    const handleReport = async (e) => {
        e.preventDefault();

        if (!selectedProperty.length || !selectedYear.length || !selectedMonth.length) {
            alert("Please select all fields");
            return;
        }

        const PID = selectedProperty.map(p => p.value);
        const Codes = selectedSource.map(s => s.value);
        const SourceNames = selectedSource.map(s => s.label);

        const doc = new jsPDF("landscape");

        const today = new Date().toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "long",
            year: "numeric"
        });

        let isFirstPage = true;

        // =========================
        // 🔥 SOURCE REPORT
        // =========================
        if (reportType === "source") {

            for (const yearObj of selectedYear) {

                const monthsArray = selectedMonth.map(m => m.value);

                // ✅ NO LOOP - direct array pass
                const payload = {
                    PID: `[${PID}]`,
                    Year: yearObj.value,
                    Month: `[${monthsArray}]`,
                    Code: Codes.length ? `["${Codes.join(",")}"]` : ""
                };
                console.log("payload", payload)
                const result = await dispatch(fetchBusinessMonthlyReport(payload));
                const finalData = result.payload || [];

                generateMultiMonthPage(
                    doc,
                    finalData,
                    selectedMonth,
                    yearObj,
                    today,
                    isFirstPage,
                    selectedProperty
                );

                isFirstPage = false;
            }

            doc.save("Source_Monthly_Report.pdf");
        }

        // =========================
        // 🔥 PROPERTY REPORT
        // =========================
        else {

            for (const yearObj of selectedYear) {

                const monthsArray = selectedMonth.map(m => m.value);

                // ✅ SAME FIX HERE
                const payload = {
                    PID: `[${PID}]`,
                    Year: yearObj.value,
                    Month: `[${monthsArray}]`,
                    Code: Codes.length ? `[${Codes.join(",")}]` : ""
                };

                const result = await dispatch(fetchBusinessPropertyMonthlyReport(payload));
                const data = result.payload || [];

                generateMultiMonthPageProperty(
                    doc,
                    data,
                    selectedMonth,
                    yearObj,
                    today,
                    isFirstPage,
                    SourceNames
                );

                isFirstPage = false;
            }

            doc.save("Property_Source_Monthly_Report.pdf");
        }
    };

    const handleClear = () => {
        setSelectedProperty([]);
        setSelectedYear([]);
        setSelectedMonth([]);
        setSelectedSource([]);
    };

    return (
        <>
            {loading && (
                <div className="fullscreen-loader">
                    <div className="fas fa-spinner fa-spin fa-3x"></div>
                </div>
            )}

            <div className="wrapper">
                <ReservationSidebar />
                <div className="main-panel">
                    <ReservationHeader />

                    <div className="container">
                        <div className="page-inner">
                            <h3 className='fw-bold'>
                                {reportType === "source"
                                    ? "Source Monthly Report"
                                    : "Property Source Monthly Report"}
                            </h3>

                            <form>
                                <div className='row mb-3'>
                                    <div className='col-md-12'>
                                        <label>Property</label>
                                        <Select
                                            options={propertyOptions}
                                            isMulti
                                            value={selectedProperty}
                                            onChange={handlePropertyChange}
                                        />
                                    </div>
                                </div>

                                <div className='row mb-3'>
                                    <div className='col-md-2'>
                                        <label>Year</label>
                                        <Select
                                            options={yearOptions}
                                            isMulti
                                            value={selectedYear}
                                            onChange={handleYearChange}
                                        />
                                    </div>

                                    <div className='col-md-4'>
                                        <label>Month</label>
                                        <Select
                                            options={monthOptions}
                                            isMulti
                                            value={selectedMonth}
                                            onChange={handleMonthChange}
                                        />
                                    </div>

                                    <div className='col-md-2'>
                                        <label>Source</label>
                                        <Select
                                            options={sourceOptions}
                                            isMulti
                                            value={selectedSource}
                                            onChange={handleSourceChange}
                                        />
                                    </div>

                                    <div className='col-md-2'>
                                        <div style={{ marginTop: "30px" }}>

                                            <input
                                                type='radio'
                                                className='me-2'
                                                name="reportType"
                                                checked={reportType === "source"}
                                                onChange={() => setReportType("source")}
                                            />
                                            <label className='me-3'>Source</label>

                                            <input
                                                type='radio'
                                                className='me-2'
                                                name="reportType"
                                                checked={reportType === "property"}
                                                onChange={() => setReportType("property")}
                                            />
                                            <label>Property</label>

                                        </div>
                                    </div>

                                    <div className='col-md-2 d-flex align-items-end'>
                                        <button onClick={handleReport} className='btn btn-primary me-2'>
                                            Report
                                        </button>

                                        <button type="button" className='btn btn-danger' onClick={handleClear}>
                                            Clear
                                        </button>
                                    </div>
                                </div>
                            </form>

                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};